import {
  useState,
  useContext,
  useCallback,
} from "react";
import { GlobalContext } from "../../Context/GlobalContext";

const useAuthApi = () => {
  const { api } = useContext(GlobalContext);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ================= SSO LOGIN =================

  const ssoLogin = useCallback(
    async (accessToken) => {
      if (!api || !accessToken) {
        console.error(
          "SSO Login API not called:",
          !api
            ? "API instance missing"
            : "Access token missing",
        );

        return null;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await api.post(
          "/User/ssoLogin",
          {
            token: accessToken,
            product: "anlook",
          },
        );

      const data = response?.data;

      if (data?.responseCode === "00") {
        localStorage.setItem(
          "anlook_sso_username",
          String(data?.response?.username ?? "").trim(),
        );
      }

      return data;
      } catch (err) {
        console.error(
          "SSO Login API Error:",
          err?.response?.data || err,
        );

        setError(
          err?.response?.data ||
            err?.message ||
            "SSO login failed",
        );

        return null;
      } finally {
        setLoading(false);
      }
    },
    [api],
  );

  // ================= GET USER ACCESS =================

  const getUserAccess = useCallback(
    async (userId) => {
      if (!api || !userId) {
        console.error(
          "Get User Access API not called:",
          !api
            ? "API instance missing"
            : "User ID missing",
        );

        return null;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await api.post(
          "/User/getUserAccess",
          {
            userid: userId,
          },
        );

        return response?.data;
      } catch (err) {
        console.error(
          "Get User Access API Error:",
          err?.response?.data || err,
        );

        setError(
          err?.response?.data ||
            err?.message ||
            "Unable to fetch user access",
        );

        return null;
      } finally {
        setLoading(false);
      }
    },
    [api],
  );

  // ================= SUBSCRIPTION DETAILS =================

  const fetchSubscriptionDetails = useCallback(
    async (userId) => {
      if (!api || !userId) {
        console.error(
          "Subscription API not called:",
          !api
            ? "API instance missing"
            : "User ID missing",
        );

        return null;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await api.post(
          "/Entitlement/fetchSubscriptionDetails",
          {
            userid: userId,
          },
        );

        return response?.data;
      } catch (err) {
        console.error(
          "Subscription API Error:",
          err?.response?.data || err,
        );

        setError(
          err?.response?.data ||
            err?.message ||
            "Unable to fetch subscription details",
        );

        return null;
      } finally {
        setLoading(false);
      }
    },
    [api],
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    loading,
    error,
    clearError,
    ssoLogin,
    getUserAccess,
    fetchSubscriptionDetails,
  };
};

export default useAuthApi;
